"use client";

import { useState, type FormEvent } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Suspense } from "react";

const H = { fontFamily: "var(--font-heading)" };

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const from = searchParams.get("from");

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword]     = useState("");
  const [error, setError]           = useState("");
  const [loading, setLoading]       = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ identifier, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        // Honor ?from= if present; otherwise use the server's role-based redirect.
        router.push(from || data.redirectTo || "/");
        router.refresh();
      } else {
        setError(data.error ?? "Invalid credentials.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(160deg, rgb(44,40,130) 0%, rgb(88,83,182) 50%, rgb(130,110,230) 80%, rgb(255,200,100) 130%)" }}
    >
      {/* Glow layers */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{ backgroundImage: "radial-gradient(ellipse at 25% 60%, rgba(255,233,34,0.12) 0%, transparent 55%), radial-gradient(ellipse at 75% 30%, rgba(255,166,89,0.10) 0%, transparent 50%)" }}
      />

      <div className="relative w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Purple header stripe */}
          <div
            className="px-8 pt-8 pb-6 text-center"
            style={{ background: "linear-gradient(135deg, rgb(44,40,130) 0%, rgb(88,83,182) 100%)" }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/images/noah-logo.png"
              alt="Noah's Ark Animal Workshop"
              className="h-20 w-auto mx-auto mb-4"
            />
            <h1
              className="text-2xl font-extrabold text-white leading-tight"
              style={H}
            >
              Build Your Event
            </h1>
            <p className="text-[rgba(255,255,255,0.75)] text-sm mt-1.5 leading-snug">
              Customize your Teddy Bear Workshop and reserve your event in just a few minutes.
            </p>
          </div>

          {/* Form */}
          <div className="px-8 py-7">
            <h2 className="text-lg font-bold text-[hsl(243,30%,12%)] mb-5" style={H}>
              Sign in to your account
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="identifier" className="text-[hsl(243,30%,15%)]">
                  Username or email
                </Label>
                <Input
                  id="identifier"
                  type="text"
                  autoComplete="username"
                  value={identifier}
                  onChange={e => setIdentifier(e.target.value)}
                  placeholder="Enter your username or email"
                  required
                  autoFocus
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="password" className="text-[hsl(243,30%,15%)]">
                    Password
                  </Label>
                  <Link
                    href="/forgot-password"
                    className="text-xs font-semibold text-[hsl(243,40%,52%)] hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
              </div>

              {error && (
                <div className="px-4 py-3 bg-[hsl(0,84%,97%)] border border-[hsl(0,60%,88%)] rounded-xl text-sm text-[hsl(0,60%,40%)]">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                size="lg"
                className="w-full bg-[hsl(243,40%,52%)] hover:bg-[hsl(243,40%,46%)] text-white mt-2"
                disabled={loading}
                style={H}
              >
                {loading ? "Signing in…" : "Sign In"}
              </Button>
            </form>
          </div>

          {/* Footer */}
          <div className="px-8 pb-6 text-center space-y-1.5">
            <p className="text-xs text-[hsl(243,15%,58%)]">
              New organizer?{" "}
              <Link href="/signup" className="font-semibold text-[hsl(243,40%,52%)] hover:underline">
                Create an account
              </Link>
            </p>
          </div>
        </div>

        {/* Version tag */}
        <p className="text-center text-xs text-white/40 mt-4">
          Noah&apos;s Ark Event Circulation Builder
        </p>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginForm />
    </Suspense>
  );
}
